import React, { useState, useEffect } from "react";
import AddonForm from "./AddonForm";
import { __ } from "@wordpress/i18n";

const AdminApp = () => {
    const [addons, setAddons] = useState([]);
    const [productId] = useState(woocommerceAddonAdmin.product_id || null);
    const [isSaving, setIsSaving] = useState(false); // State for managing save status

    // Function to fetch saved addons
    useEffect(() => {
        const fetchAddons = () => {
            const formData = new FormData();
            formData.append("action", "get_product_addons");
            formData.append("product_id", productId);
            formData.append("nonce", woocommerceAddonAdmin.nonce);

            fetch(woocommerceAddonAdmin.ajax_url, {
                method: "POST",
                body: formData,
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    return response.json();
                })
                .then((response) => {
                    if (response.success && Array.isArray(response.data.addons)) {
                        setAddons(response.data.addons);
                    }
                })
                .catch((error) => {
                    console.error(__("Error fetching addons:", "woocommerce-addon-plugin"), error);
                });
        };

        fetchAddons();
    }, [productId]);

    // Function to add a new addon
    const addNewAddon = () => {
        setAddons([
            ...addons,
            {
                id: Date.now(),
                name: "",
                type: "text",
                parentId: null,
                children: []
            }
        ]);
    };

    // Function to handle addon updates
    const handleAddonChange = (updatedAddon) => {
        setAddons(addons.map((addon) => (addon.id === updatedAddon.id ? updatedAddon : addon)));
    };

    // Function to delete an addon
    const deleteAddon = (addonId) => {
        setAddons(addons.filter((addon) => addon.id !== addonId));
    };

    // Function to save addons
    const saveAddons = () => {
        setIsSaving(true); // Set loading to true
        const formData = new FormData();
        formData.append("action", "save_product_addons");
        formData.append("product_id", productId);
        formData.append("addons", JSON.stringify(addons));
        formData.append("nonce", woocommerceAddonAdmin.nonce);

        fetch(woocommerceAddonAdmin.ajax_url, {
            method: "POST",
            body: formData,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then((response) => {
                if (response.success) {
                    alert(__("Addons saved successfully!", "woocommerce-addon-plugin"));
                } else {
                    alert(__("Error saving addons:", "woocommerce-addon-plugin") + response.data);
                }
            })
            .catch(() => {
                alert(__("An error occurred while saving addons.", "woocommerce-addon-plugin"));
            })
            .finally(() => {
                setIsSaving(false); // Set loading to false
            });
    };

    // Function to export addons as JSON
    const exportAddons = () => {
        const blob = new Blob([JSON.stringify(addons, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `addons-${productId || __("default", "woocommerce-addon-plugin")}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    // Function to handle JSON import
    const importAddons = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const importedData = JSON.parse(e.target.result);
                    if (Array.isArray(importedData)) {
                        setAddons(importedData);
                    } else {
                        alert(__("Invalid JSON format. Please upload a valid file.", "woocommerce-addon-plugin"));
                    }
                } catch (error) {
                    alert(__("Error parsing JSON file.", "woocommerce-addon-plugin"));
                }
            };
            reader.readAsText(file);
        }
    };

    return (
        <div className="addon-admin-app">
             {isSaving && (
                <div className="loader-overlay">
                    <div className="loader"></div>
                </div>
            )}
            <h2 className="title" id="addon-manager">{__("Addons Manager", "woocommerce-addon-plugin")}</h2>
            <div className="actions">
                <button type="button" className="button button-primary" onClick={addNewAddon}>
                    {__("Add New Addon", "woocommerce-addon-plugin")}
                </button>
                <div className="import-export">
                    <button type="button" className="button button-secondary" onClick={exportAddons}>
                        {__("Export Addons", "woocommerce-addon-plugin")}
                    </button>
                    <label className="button button-secondary">
                        {__("Import Addons", "woocommerce-addon-plugin")}
                        <input
                            type="file"
                            accept="application/json"
                            onChange={importAddons}
                            style={{ display: "none" }}
                        />
                    </label>
                </div>
            </div>
            <div className="addons-list">
                {addons.map((addon) => (
                    <AddonForm
                        key={addon.id}
                        addon={addon}
                        onChange={handleAddonChange}
                        onDelete={deleteAddon}
                    />
                ))}
            </div>
            <button type="button" className="button button-primary save-addons" onClick={saveAddons} disabled={isSaving}>
                {isSaving ? __("Saving...", "woocommerce-addon-plugin") : __("Save Addons", "woocommerce-addon-plugin")}
            </button>
        </div>
    );
};

export default AdminApp;
